import { ArrowUpCircle, Circle, CircleDot, MonitorPlay, RotateCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { DevicePresenceState } from '@/lib/api/types';

/*
  Device presence badge.

  The requirement is that the five states are distinguishable WITHOUT relying on colour.
  Three independent channels carry the state here, and any one of them is sufficient:

  1. Text. The state is spelled out, always, never a bare dot in a column.
  2. A distinct glyph per state, and the glyphs differ in silhouette rather than only in
     hue: a filled dot, a hollow ring, a monitor, a circular arrow, an upward arrow. They
     stay distinguishable in greyscale, at 14px, and to a red-green colour blind reader.
  3. Border weight. The two states that need action (update required, rebooting) carry a
     dashed border; the settled states are solid.

  Colour is the fourth channel and is reinforcement only.

  The glyph is aria-hidden because the text next to it already says the state. Announcing
  both would read "circle online".
*/

interface StatusMeta {
  readonly label: string;
  readonly Icon: typeof Circle;
  readonly className: string;
  readonly dashed: boolean;
  /** Extra context for assistive technology and the title attribute. */
  readonly description: string;
}

const STATUS_META: Readonly<Record<DevicePresenceState, StatusMeta>> = {
  online: {
    label: 'Online',
    Icon: CircleDot,
    className:
      'text-[var(--color-status-online)] bg-[var(--color-status-online-soft)] border-[var(--color-status-online)]/35',
    dashed: false,
    description: 'Agent is connected to a gateway and idle.',
  },
  offline: {
    label: 'Offline',
    Icon: Circle,
    className:
      'text-[var(--color-status-offline)] bg-[var(--color-status-offline-soft)] border-[var(--color-status-offline)]/35',
    dashed: false,
    description: 'No agent connection. Last seen time is in the Last Seen column.',
  },
  session_active: {
    label: 'Session active',
    Icon: MonitorPlay,
    className:
      'text-[var(--color-status-session)] bg-[var(--color-status-session-soft)] border-[var(--color-status-session)]/40',
    dashed: false,
    description: 'A remote session is in progress on this device.',
  },
  rebooting: {
    label: 'Rebooting',
    Icon: RotateCw,
    className:
      'text-[var(--color-status-reboot)] bg-[var(--color-status-reboot-soft)] border-[var(--color-status-reboot)]/45',
    dashed: true,
    description: 'Reboot in progress. The agent is expected back shortly.',
  },
  update_required: {
    label: 'Update required',
    Icon: ArrowUpCircle,
    className:
      'text-[var(--color-status-update)] bg-[var(--color-status-update-soft)] border-[var(--color-status-update)]/45',
    dashed: true,
    description: 'An agent update is pending on this device.',
  },
};

export function statusLabel(state: DevicePresenceState): string {
  return STATUS_META[state].label;
}

export interface StatusBadgeProps {
  readonly state: DevicePresenceState;
  readonly className?: string | undefined;
}

export function StatusBadge({ state, className }: StatusBadgeProps): React.JSX.Element {
  const meta = STATUS_META[state];
  const Icon = meta.Icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold whitespace-nowrap',
        meta.dashed ? 'border-dashed' : 'border-solid',
        meta.className,
        className,
      )}
      title={meta.description}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      {meta.label}
    </span>
  );
}

/** The legend under the device table. Teaches the glyphs once, so the table needs no key. */
export function StatusLegend(): React.JSX.Element {
  return (
    <dl className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {(Object.keys(STATUS_META) as DevicePresenceState[]).map((state) => {
        const meta = STATUS_META[state];
        const Icon = meta.Icon;
        return (
          <div key={state} className="flex items-center gap-1.5">
            <dt className="flex items-center gap-1.5 text-xs font-semibold">
              <Icon className={cn('size-3.5', meta.className.split(' ')[0])} aria-hidden="true" />
              <span className="text-fg">{meta.label}</span>
            </dt>
            <dd className="text-xs text-fg-subtle">{meta.description}</dd>
          </div>
        );
      })}
    </dl>
  );
}
